import React, { useState, useEffect } from 'react';
import apiService from '../services/apiService';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { cn } from '../lib/utils';
import {
  Database,
  CheckCircle,
  Zap,
  BarChart3,
  Download,
  ArrowRight,
  RefreshCcw,
  Clock,
  Inbox,
  Loader2,
  AlertCircle
} from 'lucide-react';

interface TranslationMemoryEntry {
  id: string;
  sourceText: string;
  targetText: string;
  sourceLanguage: string;
  targetLanguage: string;
  qualityScore?: number;
  useCount: number;
  lastUsed?: string;
  context?: string;
  createdAt: string;
}

interface TMStatistics {
  totalEntries: number;
  languagePairs: Array<{
    sourceLanguage: string;
    targetLanguage: string;
    count: number;
  }>;
  usageStats: {
    totalSuggestions: number;
    accepted: number;
    rejected: number;
    acceptanceRate: number;
    averageSimilarity: number;
  };
}

const TranslationMemory: React.FC = () => {
  const [sites, setSites] = useState<any[]>([]);
  const [selectedSiteId, setSelectedSiteId] = useState<string>('');
  const [statistics, setStatistics] = useState<TMStatistics | null>(null);
  const [topTranslations, setTopTranslations] = useState<TranslationMemoryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [languages, setLanguages] = useState<any[]>([]);
  const [filterSourceLang, setFilterSourceLang] = useState('');
  const [filterTargetLang, setFilterTargetLang] = useState('');

  useEffect(() => {
    loadSites();
    loadLanguages();
  }, []);

  useEffect(() => {
    if (selectedSiteId) {
      loadStatistics();
      loadTopTranslations();
    }
  }, [selectedSiteId]);

  const loadSites = async () => {
    try {
      const { sites } = await apiService.getSites(1, 100);
      setSites(sites);
      if (sites.length > 0) {
        setSelectedSiteId(sites[0].id);
      }
    } catch (err: any) {
      console.error('Error loading sites:', err);
    }
  };

  const loadLanguages = async () => {
    try {
      const langs = await apiService.getLanguages();
      setLanguages(langs);
    } catch (err) {
      console.error('Error loading languages:', err);
    }
  };

  const loadStatistics = async () => {
    if (!selectedSiteId) return;

    setLoading(true);
    setError(null);
    try {
      const stats = await apiService.getTranslationMemoryStatistics(selectedSiteId);
      setStatistics(stats);
    } catch (err: any) {
      setError(err.message || 'Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  const loadTopTranslations = async () => {
    if (!selectedSiteId) return;

    try {
      const top = await apiService.getTopTranslations(selectedSiteId, 20);
      setTopTranslations(top);
    } catch (err: any) {
      console.error('Error loading top translations:', err);
    }
  };

  const handleExportMemory = async () => {
    if (!selectedSiteId) return;

    try {
      const data = await apiService.exportTranslationMemory(
        selectedSiteId,
        filterSourceLang || undefined,
        filterTargetLang || undefined
      );

      const csvHeader = 'sourceLanguage,targetLanguage,sourceText,targetText,qualityScore,useCount,context,createdAt\n';
      const csvRows = data.map((entry: any) => {
        return [
          entry.sourceLanguage,
          entry.targetLanguage,
          `"${entry.sourceText.replace(/"/g, '""')}"`,
          `"${entry.targetText.replace(/"/g, '""')}"`,
          entry.qualityScore || '',
          entry.useCount || 0,
          `"${(entry.context || '').replace(/"/g, '""')}"`,
          entry.createdAt
        ].join(',');
      }).join('\n');

      const csv = csvHeader + csvRows;
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `translation_memory_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      alert(err.message || 'Failed to export translation memory');
    }
  };

  const getLanguageName = (code: string) => {
    const lang = languages.find(l => l.code === code);
    return lang ? lang.name : code.toUpperCase();
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground">Translation Memory</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Browse and manage your translation memory for reuse and cost savings
        </p>
      </div>

      {/* Site Selector */}
      <div className="mb-6">
        <Label className="mb-2 block">Select Site</Label>
        <select
          value={selectedSiteId}
          onChange={(e) => setSelectedSiteId(e.target.value)}
          className="flex h-10 w-full md:w-96 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <option value="">Select a site...</option>
          {sites.map(site => (
            <option key={site.id} value={site.id}>
              {site.name} ({site.domain})
            </option>
          ))}
        </select>
      </div>

      {selectedSiteId && (
        <>
          {/* Statistics Dashboard */}
          {statistics && (
            <div className="mb-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Total Entries */}
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center">
                    <div className="h-12 w-12 rounded-lg bg-muted flex items-center justify-center">
                      <Database className="h-6 w-6 text-muted-foreground" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-muted-foreground">Total Entries</p>
                      <p className="text-2xl font-semibold text-foreground">{statistics.totalEntries}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Acceptance Rate */}
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center">
                    <div className="h-12 w-12 rounded-lg bg-green-100 flex items-center justify-center">
                      <CheckCircle className="h-6 w-6 text-green-600" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-muted-foreground">Acceptance Rate</p>
                      <p className="text-2xl font-semibold text-green-600">{statistics.usageStats.acceptanceRate}%</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Total Suggestions */}
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center">
                    <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Zap className="h-6 w-6 text-primary" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-muted-foreground">Suggestions Used</p>
                      <p className="text-2xl font-semibold text-primary">{statistics.usageStats.totalSuggestions}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Avg Similarity */}
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center">
                    <div className="h-12 w-12 rounded-lg bg-purple-100 flex items-center justify-center">
                      <BarChart3 className="h-6 w-6 text-purple-600" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-muted-foreground">Avg Similarity</p>
                      <p className="text-2xl font-semibold text-purple-600">{Math.round(statistics.usageStats.averageSimilarity * 100)}%</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Language Pairs */}
          {statistics && statistics.languagePairs.length > 0 && (
            <Card className="mb-8">
              <CardHeader>
                <CardTitle>Language Pairs</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {statistics.languagePairs.map((pair, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <span className="font-medium text-foreground">
                          {getLanguageName(pair.sourceLanguage)}
                        </span>
                        <ArrowRight className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium text-foreground">
                          {getLanguageName(pair.targetLanguage)}
                        </span>
                      </div>
                      <Badge variant="secondary">{pair.count}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Export Section */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Export Translation Memory</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4 items-end">
                <div className="space-y-2">
                  <Label>Source Language (Optional)</Label>
                  <select
                    value={filterSourceLang}
                    onChange={(e) => setFilterSourceLang(e.target.value)}
                    className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    <option value="">All Languages</option>
                    {languages.map(lang => (
                      <option key={lang.code} value={lang.code}>{lang.name}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label>Target Language (Optional)</Label>
                  <select
                    value={filterTargetLang}
                    onChange={(e) => setFilterTargetLang(e.target.value)}
                    className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    <option value="">All Languages</option>
                    {languages.map(lang => (
                      <option key={lang.code} value={lang.code}>{lang.name}</option>
                    ))}
                  </select>
                </div>

                <Button onClick={handleExportMemory}>
                  <Download className="h-4 w-4 mr-2" />
                  Export to CSV
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Top Translations */}
          <Card>
            <CardHeader>
              <CardTitle>Most Reused Translations</CardTitle>
              <CardDescription>
                Your most frequently reused translations, saving time and costs
              </CardDescription>
            </CardHeader>
            <CardContent>
              {topTranslations.length === 0 ? (
                <div className="text-center py-12">
                  <Inbox className="h-12 w-12 mx-auto text-muted-foreground" />
                  <h3 className="mt-4 text-sm font-medium text-foreground">No translation memory yet</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Start translating and approved translations will be saved here for reuse.
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {topTranslations.map((entry, idx) => (
                    <div key={entry.id} className="py-4 first:pt-0 last:pb-0 hover:bg-muted/30 -mx-4 px-4 rounded">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center flex-wrap gap-2 mb-2">
                            <span className="text-lg font-bold text-muted-foreground">#{idx + 1}</span>
                            <Badge variant="secondary">
                              {getLanguageName(entry.sourceLanguage)} → {getLanguageName(entry.targetLanguage)}
                            </Badge>
                            {entry.qualityScore && (
                              <Badge
                                variant={entry.qualityScore >= 0.9 ? 'default' : entry.qualityScore >= 0.7 ? 'secondary' : 'destructive'}
                                className={cn(
                                  entry.qualityScore >= 0.9 && 'bg-green-600',
                                  entry.qualityScore >= 0.7 && entry.qualityScore < 0.9 && 'bg-yellow-500'
                                )}
                              >
                                Quality: {Math.round(entry.qualityScore * 100)}%
                              </Badge>
                            )}
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <p className="text-xs font-medium text-muted-foreground mb-1">Source</p>
                              <p className="text-sm text-foreground">{entry.sourceText}</p>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-muted-foreground mb-1">Translation</p>
                              <p className="text-sm text-foreground">{entry.targetText}</p>
                            </div>
                          </div>

                          {entry.context && (
                            <p className="mt-2 text-xs text-muted-foreground">
                              <span className="font-medium">Context:</span> {entry.context}
                            </p>
                          )}

                          <div className="mt-2 flex items-center space-x-4 text-xs text-muted-foreground">
                            <span className="flex items-center">
                              <RefreshCcw className="h-3 w-3 mr-1" />
                              Reused {entry.useCount} times
                            </span>
                            {entry.lastUsed && (
                              <span className="flex items-center">
                                <Clock className="h-3 w-3 mr-1" />
                                Last used: {new Date(entry.lastUsed).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="ml-4 flex-shrink-0 text-center">
                          <span className="text-2xl font-bold text-primary">{entry.useCount}</span>
                          <p className="text-xs text-muted-foreground">uses</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Loading State */}
      {loading && (
        <div className="text-center py-12">
          <Loader2 className="h-12 w-12 animate-spin mx-auto text-primary" />
          <p className="mt-4 text-muted-foreground">Loading translation memory...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <Card className="border-destructive/50 bg-destructive/10">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-destructive" />
              <p className="text-destructive">{error}</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TranslationMemory;
